/**
 * Form controls.
 *
 * Two things shape this file beyond looking right:
 *
 *  - **The server is the validator.** Client-side checks exist to save a round trip and to say something
 *    useful early; they are never the reason a bad value is rejected. Every command function validates
 *    again, and `RTC_VALIDATION` coming back from the database is a normal outcome to render, not a bug.
 *  - **A field says what it is for.** Every control has a real `<label>`, errors are tied to the input
 *    with `aria-describedby`, and a required field is marked in text as well as with an asterisk.
 */
import { createContext, useContext, useId, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button, cx } from './primitives';

/* ── Field ───────────────────────────────────────────────────────────────────────────────────────── */

interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
}
const FieldContext = createContext<FieldContextValue | null>(null);

export function Field({
  label, hint, error, required, children, className,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error) }}>
      <div className={cx('min-w-0', className)}>
        <label htmlFor={id} className="label-text block">
          {label}
          {required ? (
            <span className="ml-1 text-refused" title="Required">
              *
            </span>
          ) : null}
        </label>

        <div className="mt-1">{children}</div>

        {hint && !error ? (
          <p id={hintId} className="mt-1 text-small text-ink-muted">
            {hint}
          </p>
        ) : null}

        {error ? (
          <p id={errorId} className="mt-1 flex items-start gap-1 text-small text-refused">
            <AlertCircle size={12} className="mt-0.5 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

function useField() {
  return useContext(FieldContext);
}

const controlClass =
  'w-full rounded-[var(--radius-control)] border bg-surface px-2 text-body text-ink ' +
  'transition-colors duration-[120ms] disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-faint';

/* ── Controls ────────────────────────────────────────────────────────────────────────────────────── */

// `size` is a native input attribute (a number), so the height variant needs its own name.
export function Input({
  className, height = 'default', ...props
}: ComponentPropsWithoutRef<'input'> & { height?: 'default' | 'form' }) {
  const field = useField();
  return (
    <input
      id={field?.id}
      aria-describedby={field?.describedBy}
      aria-invalid={field?.invalid || undefined}
      {...props}
      className={cx(
        controlClass,
        height === 'form' ? 'h-[var(--height-control-form)]' : 'h-[var(--height-control)]',
        field?.invalid ? 'border-refused' : 'border-line-strong focus:border-brand',
        className,
      )}
    />
  );
}

export function Textarea({ className, ...props }: ComponentPropsWithoutRef<'textarea'>) {
  const field = useField();
  return (
    <textarea
      id={field?.id}
      aria-describedby={field?.describedBy}
      aria-invalid={field?.invalid || undefined}
      rows={3}
      {...props}
      className={cx(controlClass, 'py-1.5', field?.invalid ? 'border-refused' : 'border-line-strong focus:border-brand', className)}
    />
  );
}

export function Select({
  className, options, placeholder, ...props
}: Omit<ComponentPropsWithoutRef<'select'>, 'children'> & {
  options: readonly { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
}) {
  const field = useField();
  return (
    <select
      id={field?.id}
      aria-describedby={field?.describedBy}
      aria-invalid={field?.invalid || undefined}
      {...props}
      className={cx(
        controlClass,
        'h-[var(--height-control)] pr-6',
        field?.invalid ? 'border-refused' : 'border-line-strong focus:border-brand',
        className,
      )}
    >
      {/* An explicit empty option, so "not chosen" is a state the user can see and return to. */}
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function Checkbox({
  label, description, className, ...props
}: ComponentPropsWithoutRef<'input'> & { label: ReactNode; description?: ReactNode }) {
  const id = useId();
  return (
    <div className={cx('flex items-start gap-2', className)}>
      <input
        id={id}
        type="checkbox"
        {...props}
        className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-[2px] border-line-strong accent-[var(--color-brand)]"
      />
      <label htmlFor={id} className="min-w-0 text-body">
        {label}
        {description ? <span className="block text-small text-ink-muted">{description}</span> : null}
      </label>
    </div>
  );
}

export function Switch({
  checked, onChange, label, disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'relative inline-flex h-[18px] w-8 shrink-0 items-center rounded-full transition-colors duration-[120ms]',
        'disabled:cursor-not-allowed disabled:opacity-45',
        checked ? 'bg-brand' : 'bg-line-strong',
      )}
    >
      <span
        className={cx(
          'inline-block h-3.5 w-3.5 rounded-full bg-surface transition-transform duration-[120ms]',
          checked ? 'translate-x-[15px]' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

/* ── Form ────────────────────────────────────────────────────────────────────────────────────────── */

export interface FormProps {
  onSubmit: () => void | Promise<void>;
  children: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  /** A refusal from the database. `RTC_UNDECIDED_POLICY` is rendered as a decision, not a failure. */
  error?: { code?: string; message?: string } | null;
  disabled?: boolean;
  columns?: number;
}

export function Form({
  onSubmit, children, submitLabel = 'Save', cancelLabel = 'Cancel', onCancel, error, disabled, columns = 1,
}: FormProps) {
  const [busy, setBusy] = useState(false);
  const isUndecided = error?.code === 'RTC_UNDECIDED_POLICY';

  return (
    <form
      noValidate
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        try {
          await onSubmit();
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))` }}>
        {children}
      </div>

      {error ? (
        <div
          role="alert"
          className={cx(
            'mt-4 rounded-[var(--radius-control)] border px-3 py-2 text-small',
            isUndecided ? 'border-admin/30 bg-admin-ground text-admin' : 'border-refused/30 bg-refused-ground text-refused',
          )}
        >
          <p className="font-medium">
            {isUndecided ? 'This needs a decision the client has not made' : (error.code ?? 'Could not save')}
          </p>
          <p className="mt-0.5">{error.message}</p>
          {isUndecided ? (
            <p className="mt-1 opacity-90">
              The system will not guess the rule. It is tracked in the client-decisions list.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-5 flex items-center justify-end gap-2">
        {onCancel ? (
          <Button type="button" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
        ) : null}
        <Button type="submit" variant="primary" disabled={busy || disabled}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
